'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface Specification {
  category: string;
  specs: {
    label: string;
    value: string;
  }[];
}

const ProductSpecifications = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeCategory, setActiveCategory] = useState('device');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const specifications: Specification[] = [
  {
    category: 'device',
    specs: [
    { label: 'Dimensions', value: '722 x 507 x 220 mm' },
    { label: 'Weight', value: '10-12 kg' },
    { label: 'Noise Level', value: 'Below 60 dB' },
    { label: 'Device Classification', value: 'Class B' },
    { label: 'Manufacturing License', value: 'MFGMD/2023/000619' },
    { label: 'Portability', value: 'Ambulance compatible' }]
  },
  {
    category: 'technical',
    specs: [
    { label: 'Temperature Range', value: '30°C - 35°C (configurable cooling)' },
    { label: 'Cooling Duration', value: '1-100 hours' },
    { label: 'Time to Cool', value: '30-60 minutes' },
    { label: 'Re-warming Time', value: '6-10 hours' },
    { label: 'Fluid Circuit Pressure', value: '25 psi' },
    { label: 'Reservoir Capacity', value: '650 ml' }]
  },
  {
    category: 'safety',
    specs: [
    { label: 'Regulatory Compliance', value: 'EN ISO standards certified' },
    { label: 'Interface Design', value: 'NICU friendly interface' },
    { label: 'Alert System', value: 'Audio & Visual alerts' },
    { label: 'Operating Temperature', value: '15°C - 30°C environment' },
    { label: 'Treatment Modes', value: 'Quick Start and Normal (configurable)' },
    { label: 'Safety Standards', value: 'Device Class B compliant' }]
  },
  {
    category: 'clinical',
    specs: [
    { label: 'Brain Monitoring', value: 'Amplitude Integrated EEG (aEEG)' },
    { label: 'Electrodes', value: '5 electrodes for cerebral function monitoring' },
    { label: 'Data Storage', value: '~2000 hours recording capacity' },
    { label: 'Data Export', value: 'USB 2.0 interface' },
    { label: 'Treatment Protocol', value: 'Whole body cooling with integrated brain monitoring' },
    { label: 'Clinical Application', value: 'Hypoxic Ischemic Encephalopathy (HIE) in newborns' }]
  }];


  const technicalAdvantages = [
  {
    icon: 'CpuChipIcon',
    title: 'Integrated Brain Monitoring',
    description: 'World\'s first whole body cooling device with in-built Amplitude Integrated EEG (aEEG) for real-time cerebral function monitoring during therapeutic hypothermia.'
  },
  {
    icon: 'SignalIcon',
    title: 'Precise Temperature Control',
    description: 'Configurable cooling range (30°C-35°C) with rapid cooling capability (30-60 min) and controlled re-warming (6-10 hours) for optimal neuroprotection.'
  },
  {
    icon: 'ShieldCheckIcon',
    title: 'NICU-Optimized Design',
    description: 'Compact, portable system with below 60dB noise level, NICU-friendly interface, and ambulance compatibility for seamless patient transport.'
  },
  {
    icon: 'ArrowPathIcon',
    title: 'Comprehensive Data Management',
    description: 'Extensive data storage (~2000 hours) with USB 2.0 export capability for clinical documentation and treatment analysis.'
  }];


  const categories = [
  { id: 'device', label: 'Device Specifications', icon: 'CpuChipIcon' },
  { id: 'technical', label: 'Technical Features', icon: 'WrenchScrewdriverIcon' },
  { id: 'safety', label: 'Safety & Compliance', icon: 'ShieldCheckIcon' },
  { id: 'clinical', label: 'Clinical Implementation', icon: 'BeakerIcon' }];


  const currentSpecs = specifications.find((s) => s.category === activeCategory) || specifications[0];

  const deviceImages = [
  {
    src: '/assets/images/Revive_in_Hospital_5-1766903195945.jpeg',
    alt: 'REVIVE medical device control unit with touchscreen display showing 36.2°C core body temperature monitoring in clinical hospital setting with newborn patient receiving whole body cooling therapy',
    caption: 'REVIVE System with in-built Brain monitoring'
  },
  {
    src: '/assets/images/Revive_in_Hospital_1-1766903240466.jpg',
    alt: 'Hospital NICU setting showing newborn baby receiving whole body cooling therapy on blue REVIVE cooling blanket system, white and blue REVIVE control unit with black touchscreen display showing temperature monitoring, multiple monitoring sensors and cables connected to patient',
    caption: 'Complete REVIVE Therapeutic Hypothermia System in Clinical Use'
  }];


  return (
    <section id="device-technology" className="py-24 bg-surface">
      <div className="max-w-[1200px] mx-auto px-6">
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-2xl mb-6 brain-activity-glow">
            <Icon name="CpuChipIcon" size={20} />
            <span className="text-sm font-semibold">Advanced Technology</span>
          </div>

          <h2 className="lg:text-5xl font-bold font-heading text-foreground mb-6 text-[32px]">
            Product Specifications & Technical Advantages
          </h2>

          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Engineered with precision for the unique demands of neonatal care, combining cutting-edge technology with clinical practicality.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 mb-16">
          <div className="space-y-4">
            {deviceImages.map((image, idx) =>
            <div key={idx} className="relative h-[240px] rounded-2xl overflow-hidden brain-activity-glow">
                <AppImage
                src={image.src}
                alt={image.alt}
                className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-neuro-primary/60 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 text-primary-foreground">
                  <h3 className="text-lg font-bold font-heading">{image.caption}</h3>
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            {technicalAdvantages.map((advantage, index) =>
            <div key={index} className="bg-card rounded-2xl p-6 hover:shadow-card transition-all duration-250 ease-out border border-neuro-accent/20 hover:border-neuro-accent/40">
                <div className="p-3 bg-neuro-primary/10 rounded-2xl inline-flex mb-4">
                  <Icon name={advantage.icon as any} size={24} className="text-neuro-primary" />
                </div>
                <h4 className="font-bold text-foreground mb-2">{advantage.title}</h4>
                <p className="text-sm text-muted-foreground">{advantage.description}</p>
              </div>
            )}
          </div>
        </div>

        <div className="bg-card rounded-2xl shadow-card overflow-hidden">
          <div className="border-b border-border neuro-gradient">
            <div className="flex overflow-x-auto">
              {categories.map((category) =>
              <button
                key={category.id}
                onClick={isHydrated ? () => setActiveCategory(category.id) : undefined}
                className={`flex-1 min-w-[200px] px-6 py-4 font-semibold transition-all duration-250 ease-out ${
                activeCategory === category.id ?
                'bg-neuro-primary text-primary-foreground' :
                'text-primary-foreground/70 hover:bg-neuro-secondary'}`
                }>

                  <div className="flex items-center justify-center space-x-2">
                    <Icon name={category.icon as any} size={20} />
                    <span className="hidden md:inline">{category.label}</span>
                  </div>
                </button>
              )}
            </div>
          </div>

          <div className="p-8 monitoring-grid">
            <div className="grid md:grid-cols-2 gap-6">
              {currentSpecs.specs.map((spec, index) =>
              <div key={index} className="flex items-start space-x-4 p-4 bg-surface rounded-2xl border border-neuro-accent/20">
                  <div className="p-2 bg-neuro-primary/10 rounded-2xl flex-shrink-0">
                    <Icon name="CheckCircleIcon" size={20} className="text-neuro-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground mb-1">{spec.label}</h4>
                    <p className="text-sm text-muted-foreground">{spec.value}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mt-16 nicu-monitor-bg rounded-2xl p-8 text-center text-primary-foreground">
          <Icon name="DocumentTextIcon" size={48} className="mx-auto mb-4" />
          <h3 className="text-2xl font-bold font-heading mb-4">
            Request Detailed Technical Documentation
          </h3>
          <p className="text-lg opacity-90 mb-6 max-w-2xl mx-auto">
            Download complete product specifications, technical manuals, and integration guides for your evaluation process.
          </p>
          <a
            href="https://hrauqraoxhemxiizruob.supabase.co/storage/v1/object/public/Documents/Revive_brochure.pdf"
            target="_blank"
            rel="noopener noreferrer"
            download
            className="bg-accent text-accent-foreground px-8 py-4 rounded-2xl font-cta font-bold text-lg shadow-cta hover:bg-accent/90 transition-all duration-250 ease-out inline-block"
          >
            Download Technical Package
          </a>
        </div>
      </div>
    </section>);

};

export default ProductSpecifications;